import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Empty, Spin, Table, Typography } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../api/http';
import { getAvailability, getRecordedFailures, getServiceHistory } from '../api/reports';
import { downloadCsv, rowsToCsv } from '../utils/reportCsv';
import { parseCalendarDate } from '../utils/recordTemporal';
import { DashboardPageShell, DashboardPanel } from '../components/dashboard';
import { useAuth } from '../auth/AuthProvider';
import { resolveActiveCustomerContext } from '../auth/customerContext';
import { formatPrintDate } from './PrintRecordParts';
import './ReportsPage.css';

const REPORTS = { service: 'Fleet History', failures: 'Recorded Failures', availability: 'Availability / Downtime' };
const EXPORT_PAGE_LIMIT = 100;

function validReportDate(value) {
  return (parseCalendarDate(value)?.year || 0) > 0;
}

function ReportDateInput({ label, value, onCommit }) {
  const inputRef = useRef(null);
  const { key: locationKey } = useLocation();

  // Let the native control own its segmented editing buffer. Only an external
  // committed date change (including browser navigation) should replace it.
  useEffect(() => { inputRef.current.value = value; }, [value, locationKey]);

  const commit = (input) => {
    if (!input.validity.valid || !validReportDate(input.value)) {
      input.value = value;
      return;
    }
    if (input.value !== value) onCommit(input.value);
  };

  return <input ref={inputRef} aria-label={label} type="date" defaultValue={value}
    onBlur={(event) => commit(event.currentTarget)}
    onKeyDown={(event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit(event.currentTarget);
      }
    }} />;
}

function isoDate(date) {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
}

export function defaultReportDates(now = new Date()) {
  const from = new Date(now);
  from.setDate(from.getDate() - 90);
  return { from: isoDate(from), to: isoDate(now) };
}
export function defaultAvailabilityDates(now = new Date(), timeZone = 'UTC') {
  const toText=new Intl.DateTimeFormat('en-CA',{timeZone,year:'numeric',month:'2-digit',day:'2-digit'}).format(now);
  const to = new Date(`${toText}T00:00:00Z`); const from = new Date(to); from.setUTCDate(from.getUTCDate() - 90);
  return { from: from.toISOString().slice(0, 10), to: toText };
}

function displayValue(value) { return value === null || value === undefined || value === '' ? '—' : String(value); }
function label(value) {
  if (value === 'OPERATOR_CHECKLIST') return 'Operator Checklist';
  if (value === 'FAILURES_RECORDED') return 'Failures Recorded';
  if (value === 'NO_FAILURES_RECORDED') return 'No Failures Recorded';
  return displayValue(value).replaceAll('_', ' ');
}
function csvLabel(value) { return displayValue(value).replaceAll('_', ' '); }
function hours(minutes) { return minutes == null ? '—' : (minutes / 60).toFixed(1); }
export function availabilityTimestamp(value, timeZone) {
  if (!value || !timeZone) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' }).format(date);
  } catch {
    return '—';
  }
}

export function availabilityTimezoneLabel(timeZone, referenceTimestamp) {
  if (!timeZone) return '—';
  const date = new Date(referenceTimestamp);
  if (Number.isNaN(date.getTime())) return timeZone;
  try {
    const zoneName = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'long' })
      .formatToParts(date).find((part) => part.type === 'timeZoneName')?.value;
    return zoneName ? `${zoneName} (${timeZone})` : timeZone;
  } catch {
    return timeZone;
  }
}

export function recordPath(item, report) {
  const id = encodeURIComponent(item.recordId);
  if ((report === 'failures' && item.source === 'OPERATOR_CHECKLIST') || item.type === 'OPERATOR_CHECKLIST') return `/customer/operator-checklist/view?id=${id}`;
  if (report === 'failures' || item.type === 'MAINTENANCE') return `/customer/maintenance/view?id=${id}`;
  if (item.type === 'REPAIR') return `/customer/repair/view?id=${id}`;
  return null;
}

function ReportsMobileList({ ariaLabel, emptyDescription, items, renderItem, rowKey }) {
  return <section className="reports-mobile-list" aria-label={ariaLabel}>
    {items.length === 0 ? <Empty description={emptyDescription} /> : items.map((item) => <article className="reports-mobile-card" key={rowKey(item)}>
      {renderItem(item)}
    </article>)}
  </section>;
}

function MobileCardHeader({ unit, aside }) {
  return <header className="reports-mobile-card-header">
    <strong className="reports-mobile-card-unit">{unit}</strong>
    <span className="reports-mobile-card-aside">{aside}</span>
  </header>;
}

function MobileBadge({ children }) {
  return <span className="reports-mobile-badge">{children}</span>;
}

function MobileMetadata({ children, className = '' }) {
  return <dl className={`reports-mobile-metadata ${className}`.trim()}>{children}</dl>;
}

function MobileMetadataItem({ label: itemLabel, value }) {
  return <div><dt>{itemLabel}</dt><dd>{value}</dd></div>;
}

export default function ReportsPage() {
  const { session } = useAuth();
  const customerName = resolveActiveCustomerContext(session).customerName;
  const [params, setParams] = useSearchParams();
  const requestedReport = params.get('report');
  const report = requestedReport === null
    ? 'availability'
    : ['failures', 'availability'].includes(requestedReport) ? requestedReport : 'service';
  const defaults = useMemo(() => report === 'availability' ? defaultAvailabilityDates() : defaultReportDates(), [report]);
  const filters = {
    from: params.get('from') || defaults.from,
    to: params.get('to') || defaults.to,
    forkliftId: params.get('forkliftId') || '',
    type: ['ALL', 'MAINTENANCE', 'REPAIR', 'OPERATOR_CHECKLIST'].includes(params.get('type')) ? params.get('type') : 'ALL',
    source: ['ALL', 'OPERATOR_CHECKLIST', 'MAINTENANCE'].includes(params.get('source')) ? params.get('source') : 'ALL',
    page: Math.max(0, Number.parseInt(params.get('page') || '0', 10) || 0),
  };
  const [forklifts, setForklifts] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [scheduleRequired,setScheduleRequired]=useState(null);
  const { from, to, forkliftId, type, source, page } = filters;
  const requestFilters = useMemo(() => ({ from, to, forkliftId, type, source, page }), [from, to, forkliftId, type, source, page]);

  const update = useCallback((changes, resetPage = true) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
    if (resetPage) next.set('page', '0');
    setParams(next);
  }, [params, setParams]);

  useEffect(() => {
    const canonical = new URLSearchParams({ report, from, to });
    if (forkliftId) canonical.set('forkliftId', forkliftId);
    if (report !== 'availability') canonical.set(report === 'service' ? 'type' : 'source', report === 'service' ? type : source);
    canonical.set('page', String(page));
    if (canonical.toString() !== params.toString()) setParams(canonical, { replace: true });
  }, [forkliftId, from, page, params, report, setParams, source, to, type]);

  useEffect(() => {
    api.get('/api/forklifts').then(({ data }) => setForklifts(Array.isArray(data) ? data : [])).catch(() => setForklifts([]));
  }, []);

  useEffect(() => {
    if(report!=='availability'||params.get('from')||params.get('to'))return;
    api.get('/api/customers/me/operating-schedule',{skipCustomer:true}).then(({data})=>{
      if(data?.timezone)update(defaultAvailabilityDates(new Date(),data.timezone));
    }).catch(()=>{});
  },[params,report,update]);

  useEffect(() => {
    setScheduleRequired(null);
    if (!validReportDate(requestFilters.from) || !validReportDate(requestFilters.to)) { setError('Enter complete, valid From and To dates.'); setLoading(false); setResult(null); return; }
    if (requestFilters.from > requestFilters.to) { setError('From date must be on or before To date.'); setLoading(false); setResult(null); return; }
    let active = true;
    setLoading(true); setError(''); setResult(null);
    const request = report === 'service' ? getServiceHistory(requestFilters) : report === 'failures' ? getRecordedFailures(requestFilters) : getAvailability(requestFilters);
    request.then(({ data }) => { if (active) setResult(data); }).catch(async (e) => {
      if (!active) return;
      setResult(null);
      if (report === 'availability' && e.status === 409 && e.data?.message === 'Operating schedule required') {
        let coverage = { state: 'unknown' };
        try {
          const { data } = await api.get('/api/customers/me/operating-schedule', { skipCustomer: true });
          if (data?.configured === false) coverage = { state: 'missing' };
          else if (data?.effectiveFrom && data?.timezone) coverage = { state: 'history', earliest: data.earliestEffectiveFrom };
        } catch { /* Keep the message neutral when schedule configuration cannot be confirmed. */ }
        if (active) setScheduleRequired(coverage);
      } else setError('We could not load this report. Please try again.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [report, requestFilters]);

  const availabilityResult = report === 'availability' && result?.context && result?.units ? result : null;
  const reportTimezone = availabilityResult?.context.reportTimezone;
  const formatAvailabilityTimestamp = (value) => availabilityTimestamp(value, reportTimezone);
  const pageResult = report === 'availability' ? availabilityResult?.units : result;
  const content = Array.isArray(pageResult?.content) ? pageResult.content : [];
  const forkliftName = forklifts.find((f) => String(f.id) === filters.forkliftId)?.unitId || 'All units';
  const resultRowKey = report === 'availability'
    ? (item) => item.forkliftId ?? item.unitId
    : report === 'failures'
      ? (item) => `${item.recordId}-${item.failedItem}`
      : (item) => item.recordId;
  const linkCell = (value, item) => {
    const path = recordPath(item, report);
    return path ? <Link to={path} state={{ returnTo: `/customer/reports?${params}` }}>{displayValue(value)}</Link> : displayValue(value);
  };
  const columns = report === 'service' ? [
    { title: 'Date', dataIndex: 'date', render: formatPrintDate }, { title: 'Unit', dataIndex: 'unitId', render: linkCell },
    { title: 'Type', dataIndex: 'type', render: label, csvEnum: true }, { title: 'Status', dataIndex: 'status', render: label, csvEnum: true },
    { title: 'Summary / Work / Notes', dataIndex: 'summary', render: displayValue },
    { title: 'Technician / Performed By', dataIndex: 'performedByName', render: displayValue },
    { title: 'Hour Meter', dataIndex: 'hourMeter', render: displayValue },
    { title: 'Submission Source', dataIndex: 'submissionSource', render: label, csvEnum: true },
  ] : report === 'failures' ? [
    { title: 'Date', dataIndex: 'date', render: formatPrintDate }, { title: 'Unit', dataIndex: 'unitId', render: linkCell },
    { title: 'Failed Item', dataIndex: 'failedItem', render: displayValue }, { title: 'Source', dataIndex: 'source', render: label, csvEnum: true },
    { title: 'Performed By', dataIndex: 'performedByName', render: displayValue }, { title: 'Notes', dataIndex: 'notes', render: displayValue },
  ] : [
    { title: 'Unit', dataIndex: 'unitId', render: displayValue },
    { title: 'Current Status', dataIndex: 'currentStatus', render: label },
    { title: 'Current Status Duration', dataIndex: 'currentStatusDurationMinutes', render: hours },
    { title: 'Uptime', render: (_, row) => hours(row.metrics.uptimeMinutes) },
    { title: 'Availability', render: (_, row) => row.metrics.availabilityPercent == null ? 'N/A' : `${row.metrics.availabilityPercent}%`,
      sorter: (a, b) => (a.metrics.availabilityPercent ?? Infinity) - (b.metrics.availabilityPercent ?? Infinity) },
    { title: 'Total Downtime', render: (_, row) => hours(row.metrics.totalDowntimeMinutes),
      sorter: (a, b) => a.metrics.totalDowntimeMinutes - b.metrics.totalDowntimeMinutes },
    { title: 'DOWN', render: (_, row) => hours(row.metrics.downMinutes) },
    { title: 'Waiting on Parts', render: (_, row) => hours(row.metrics.waitingOnPartsMinutes) },
    { title: 'Downtime Events', render: (_, row) => row.metrics.downtimeEventCount },
    { title: 'Excluded Retired Time', render: (_, row) => hours(row.metrics.excludedRetiredMinutes) },
    { title: 'Status tracking began', dataIndex: 'statusTrackingStartedAt', render: formatAvailabilityTimestamp },
  ];
  const historyColumns = [
    { title: 'Date / Time', dataIndex: 'changedAt', render: formatAvailabilityTimestamp }, { title: 'Unit', dataIndex: 'unitId' },
    { title: 'From Status', dataIndex: 'fromStatus', render: label }, { title: 'To Status', dataIndex: 'toStatus', render: label },
    { title: 'Duration in Resulting Status', dataIndex: 'resultingStatusDurationMinutes', render: hours },
  ];
  const renderMobileItem = report === 'service' ? (item) => <>
    <MobileCardHeader unit={linkCell(item.unitId, item)} aside={formatPrintDate(item.date)} />
    <div className="reports-mobile-badges"><MobileBadge>{label(item.type)}</MobileBadge><MobileBadge>{label(item.status)}</MobileBadge></div>
    <section className="reports-mobile-primary"><span>Summary / Work / Notes</span><p>{displayValue(item.summary)}</p></section>
    <MobileMetadata className="reports-mobile-metadata-grid">
      <MobileMetadataItem label="Technician / Performed By" value={displayValue(item.performedByName)} />
      <MobileMetadataItem label="Hour Meter" value={displayValue(item.hourMeter)} />
      <MobileMetadataItem label="Submission Source" value={label(item.submissionSource)} />
    </MobileMetadata>
  </> : report === 'failures' ? (item) => <>
    <MobileCardHeader unit={linkCell(item.unitId, item)} aside={formatPrintDate(item.date)} />
    <div className="reports-mobile-badges"><MobileBadge>{label(item.source)}</MobileBadge></div>
    <section className="reports-mobile-primary reports-mobile-failure-content">
      <div><span>Failed Item</span><p>{displayValue(item.failedItem)}</p></div>
      <div><span>Notes</span><p>{displayValue(item.notes)}</p></div>
    </section>
    <MobileMetadata><MobileMetadataItem label="Performed By" value={displayValue(item.performedByName)} /></MobileMetadata>
  </> : (item) => <>
    <MobileCardHeader unit={displayValue(item.unitId)} aside={<MobileBadge>{label(item.currentStatus)}</MobileBadge>} />
    <section className="reports-mobile-availability"><span>Availability</span><strong>{item.metrics.availabilityPercent == null ? 'N/A' : `${item.metrics.availabilityPercent}%`}</strong></section>
    <MobileMetadata className="reports-mobile-metrics-grid">
      <MobileMetadataItem label="Current Status Duration" value={hours(item.currentStatusDurationMinutes)} />
      <MobileMetadataItem label="Uptime" value={hours(item.metrics.uptimeMinutes)} />
      <MobileMetadataItem label="Total Downtime" value={hours(item.metrics.totalDowntimeMinutes)} />
      <MobileMetadataItem label="DOWN" value={hours(item.metrics.downMinutes)} />
      <MobileMetadataItem label="Waiting on Parts" value={hours(item.metrics.waitingOnPartsMinutes)} />
      <MobileMetadataItem label="Downtime Events" value={item.metrics.downtimeEventCount} />
    </MobileMetadata>
    <MobileMetadata className="reports-mobile-secondary-details">
      <MobileMetadataItem label="Excluded Retired Time" value={hours(item.metrics.excludedRetiredMinutes)} />
      <MobileMetadataItem label="Status tracking began" value={formatAvailabilityTimestamp(item.statusTrackingStartedAt)} />
    </MobileMetadata>
  </>;
  const renderHistoryMobileItem = (item) => <>
    <MobileCardHeader unit={displayValue(item.unitId)} aside={formatAvailabilityTimestamp(item.changedAt)} />
    <div className="reports-mobile-transition" aria-label={`${label(item.fromStatus)} to ${label(item.toStatus)}`}>
      <strong>{label(item.fromStatus)}</strong><span aria-hidden="true">→</span><strong>{label(item.toStatus)}</strong>
    </div>
    <MobileMetadata><MobileMetadataItem label="Duration in Resulting Status" value={hours(item.resultingStatusDurationMinutes)} /></MobileMetadata>
  </>;

  const exportReport = async () => {
    setExporting(true); setError('');
    try {
      if (report === 'availability') {
        const units = []; let exportPage = 0; let pages = 1; let exportData;
        do {
          ({ data: exportData } = await getAvailability({ ...filters, page: exportPage }, 100));
          units.push(...(exportData.units?.content || [])); pages = exportData.units?.totalPages || 0; exportPage += 1;
        } while (exportPage < pages && exportPage < EXPORT_PAGE_LIMIT);
        if (exportPage < pages) throw new Error('limit');
        const events = exportData?.statusHistory || []; const context = exportData?.context || {};
        const exportTimezone = context.reportTimezone;
        const exportTimestamp = (value) => availabilityTimestamp(value, exportTimezone);
        const headers = ['Row Type', 'From', 'To', 'Generated At (Report Timezone)', 'Report Timezone', 'Operating Schedule', 'Unit', 'Status Tracking Started At (Report Timezone)',
          'Current Status', 'Current Status Duration Hours', 'Uptime Hours', 'Availability %', 'Total Downtime Hours',
          'DOWN Hours', 'Waiting on Parts Hours', 'Downtime Events', 'Excluded Retired Hours', 'Event Timestamp (Report Timezone)',
          'From Status', 'To Status', 'Resulting Status Duration Hours'];
        const common = [context.from, context.to, exportTimestamp(context.generatedAt), context.reportTimezone,context.operatingScheduleSummary];
        const rows = units.map((u) => ['UNIT_SUMMARY', ...common, u.unitId, exportTimestamp(u.statusTrackingStartedAt), u.currentStatus,
          hours(u.currentStatusDurationMinutes), hours(u.metrics.uptimeMinutes), u.metrics.availabilityPercent ?? 'N/A',
          hours(u.metrics.totalDowntimeMinutes), hours(u.metrics.downMinutes), hours(u.metrics.waitingOnPartsMinutes),
          u.metrics.downtimeEventCount, hours(u.metrics.excludedRetiredMinutes), '', '', '', '']);
        events.forEach((e) => rows.push(['STATUS_EVENT', ...common, e.unitId, '', '', '', '', '', '', '', '', '', '',
          exportTimestamp(e.changedAt), e.fromStatus, e.toStatus, hours(e.resultingStatusDurationMinutes)]));
        downloadCsv(`availability-downtime-${filters.from}-${filters.to}.csv`, rowsToCsv(headers, rows)); return;
      }
      const fetcher = report === 'service' ? getServiceHistory : getRecordedFailures;
      const all = [];
      let page = 0; let pages = 1;
      do {
        const { data } = await fetcher({ ...filters, page }, 100);
        all.push(...(data.content || [])); pages = data.totalPages || 0; page += 1;
      } while (page < pages && page < EXPORT_PAGE_LIMIT);
      if (page < pages) throw new Error('limit');
      const headers = columns.map((c) => c.title);
      const rows = all.map((item) => columns.map((c) => c.csvEnum ? csvLabel(item[c.dataIndex]) : displayValue(item[c.dataIndex])));
      const filename = report === 'service' ? 'fleet-history' : 'recorded-failures';
      downloadCsv(`${filename}-${filters.from}-${filters.to}.csv`, rowsToCsv(headers, rows));
    } catch { setError('We could not export this report. Please try again.'); }
    finally { setExporting(false); }
  };

  const filterKey = report === 'service' ? 'type' : 'source';
  const filterOptions = report === 'service' ? ['ALL', 'MAINTENANCE', 'REPAIR', 'OPERATOR_CHECKLIST'] : ['ALL', 'OPERATOR_CHECKLIST', 'MAINTENANCE'];
  const availabilityScheduleRequired = report === 'availability' && !!scheduleRequired;
  return <DashboardPageShell title={customerName} productDashboardShell="customer" shellClassName="reports-page-shell">
    <DashboardPanel className="dashboard-fleet-dashboard-panel reports-page reports-print-surface">
      <div className="reports-controls">
        <div className="reports-heading"><Typography.Title level={2}>Reports</Typography.Title><Typography.Text type="secondary">Review and export submitted fleet records.</Typography.Text></div>
        <form className="reports-filters no-print" onSubmit={(e) => e.preventDefault()}>
          <label className="reports-selector">Report
            <select aria-label="Report" value={report} onChange={(event) => {
              const value = event.target.value;
              update({ report: value, ...(value === 'service' ? { type: 'ALL' } : value === 'failures' ? { source: 'ALL' } : {}) });
            }}>
              {Object.entries(REPORTS).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
            </select>
          </label>
          <label>From<ReportDateInput key={`${report}-from`} label="From" value={filters.from} onCommit={(value) => update({ from: value })} /></label>
          <label>To<ReportDateInput key={`${report}-to`} label="To" value={filters.to} onCommit={(value) => update({ to: value })} /></label>
          <label>Forklift / Unit<select aria-label="Forklift / Unit" value={filters.forkliftId} onChange={(e) => update({ forkliftId: e.target.value })}><option value="">All units</option>{forklifts.map((f) => <option key={f.id} value={f.id}>{displayValue(f.unitId)}</option>)}</select></label>
          {report !== 'availability' && <label>{report === 'service' ? 'Type' : 'Source'}<select aria-label={report === 'service' ? 'Type' : 'Source'} value={filters[filterKey]} onChange={(e) => update({ [filterKey]: e.target.value })}>{filterOptions.map((v) => <option key={v} value={v}>{label(v)}</option>)}</select></label>}
        </form>
        <div className="reports-actions no-print"><Button icon={<DownloadOutlined />} loading={exporting} disabled={loading || !!error || availabilityScheduleRequired} onClick={exportReport}>Export CSV</Button><Button icon={<PrinterOutlined />} disabled={availabilityScheduleRequired} onClick={() => window.print()}>Print</Button></div>
      </div>
      <section className="reports-print-context" aria-label="Report context">
        <h2>{REPORTS[report]}</h2>
        <dl>
          <div><dt>Report</dt><dd>{REPORTS[report]}</dd></div>
          <div><dt>From</dt><dd>{formatPrintDate(filters.from)}</dd></div>
          <div><dt>To</dt><dd>{formatPrintDate(filters.to)}</dd></div>
          <div><dt>Forklift / Unit</dt><dd>{forkliftName}</dd></div>
          {report !== 'availability' && <div><dt>{report === 'service' ? 'Type' : 'Source'}</dt><dd>{label(filters[filterKey])}</dd></div>}
          {report === 'availability' && <><div><dt>Report timezone</dt><dd>{availabilityTimezoneLabel(reportTimezone, availabilityResult?.context.generatedAt)}</dd></div><div><dt>Operating schedule</dt><dd>{availabilityResult?.context.operatingScheduleSummary||'—'}</dd></div><div><dt>Generated at</dt><dd>{formatAvailabilityTimestamp(availabilityResult?.context.generatedAt)}</dd></div></>}
        </dl>
      </section>
      {availabilityResult?.summary && <section className="availability-summary" aria-label="Availability summary">
        <div><strong>Availability %</strong><span>{availabilityResult.summary.availabilityPercent == null ? 'N/A' : `${availabilityResult.summary.availabilityPercent}%`}</span></div>
        <div><strong>Uptime Hours</strong><span>{hours(availabilityResult.summary.uptimeMinutes)}</span></div>
        <div><strong>Total Downtime</strong><span>{hours(availabilityResult.summary.totalDowntimeMinutes)}</span></div>
        <div><strong>DOWN</strong><span>{hours(availabilityResult.summary.downMinutes)}</span></div>
        <div><strong>Waiting on Parts</strong><span>{hours(availabilityResult.summary.waitingOnPartsMinutes)}</span></div>
        <div><strong>Downtime Events</strong><span>{availabilityResult.summary.downtimeEventCount}</span></div>
        <div><strong>Excluded Retired Time</strong><span>{hours(availabilityResult.summary.excludedRetiredMinutes)}</span></div>
      </section>}
      {availabilityResult && <Alert type="info" showIcon message={`Operating schedule: ${availabilityResult.context.operatingScheduleSummary}`} description="Availability metrics include scheduled operating time only. Status History shows actual status transitions." />}
      {availabilityScheduleRequired ? <Alert type="warning" showIcon
        message={scheduleRequired.state === 'missing' ? 'Operating schedule required' : scheduleRequired.state === 'history' ? 'Selected start date is before operating schedule history' : 'Operating schedule coverage could not be confirmed'}
        description={<span>{scheduleRequired.state === 'missing'
          ? 'No Operating Schedule is configured. Configure one before using Availability / Downtime.'
          : scheduleRequired.state === 'history'
            ? <>An Operating Schedule is configured, but Availability / Downtime is unavailable for the portion of history before it applies. {scheduleRequired.earliest && <>The earliest applicable schedule date is {formatPrintDate(scheduleRequired.earliest)}. </>}Choose a report start date covered by schedule history.</>
            : 'We could not confirm whether an Operating Schedule is configured or covers the selected start date. Review your schedule and try again.'} <Link to="/customer/settings#operations">Settings → Operations</Link></span>} /> : error ? <Alert type="error" showIcon message={error} /> : loading ? <div className="reports-state" role="status"><Spin /> <span>Loading {REPORTS[report].toLowerCase()}…</span></div> : content.length === 0 ? <Empty description={`No ${REPORTS[report].toLowerCase()} found for the selected filters.`} /> : <>
        <div className="reports-table-wrap reports-desktop-table"><Table rowKey={resultRowKey} columns={columns} dataSource={content} pagination={false} /></div>
        <ReportsMobileList ariaLabel={`${REPORTS[report]} mobile results`} items={content} renderItem={renderMobileItem} rowKey={resultRowKey} />
      </>}
      {!loading && !error && <nav className="reports-pagination no-print" aria-label="Report pagination"><Button disabled={pageResult?.first || filters.page === 0} onClick={() => update({ page: String(filters.page - 1) }, false)}>Previous</Button><span>Page {filters.page + 1}{pageResult?.totalPages ? ` of ${pageResult.totalPages}` : ''}{Number.isFinite(pageResult?.totalElements) ? ` · ${pageResult.totalElements} records` : ''}</span><Button disabled={pageResult?.last ?? content.length === 0} onClick={() => update({ page: String(filters.page + 1) }, false)}>Next</Button></nav>}
      {report === 'availability' && !loading && !error && !availabilityScheduleRequired && <section className="availability-history"><Typography.Title level={3}>Status History</Typography.Title><div className="reports-table-wrap reports-desktop-table"><Table rowKey="auditEventId" columns={historyColumns} dataSource={availabilityResult?.statusHistory || []} pagination={false} /></div><ReportsMobileList ariaLabel="Availability Status History mobile results" emptyDescription="No status history found for the selected filters." items={availabilityResult?.statusHistory || []} renderItem={renderHistoryMobileItem} rowKey={(item) => item.auditEventId} /></section>}
    </DashboardPanel>
  </DashboardPageShell>;
}
